import { FC, useMemo, useState } from "react";
import { Box, Button, Chip, Divider, IconButton, MenuItem, styled } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays, faChevronDown, faChevronUp, faCodeBranch } from "@fortawesome/duotone-light-svg-icons";
import { Checkbox, InputOnlyField, Markdown, Select, Text } from "@ngt/forms";
import ConfigurationFormEditPanel, { ConfigurationBox, ConfigurationTitle } from './ConfigurationFormEditPanel';
import { ConfigurationType, IConfigurationVersion } from "@/utilities/forms/ConfigurationFormProcessor";
import { IConfigurationFormEditContext } from "@/contexts/forms/ConfigurationFormEditContext";
import { IEventMetadata, IFormMetadata, IFormVersionMetadata, SourceDataPriority } from "@/api/dtos";
import { useFormActions } from "@ngt/forms-core";
import { ISourceDataConfigurationFormVersion } from '../../../../api/dtos';
import ConfigurationFormEditInfo, { ConfigurationDividerBox, ConfigurationFlexBox, OtherVersionsBox, OtherVersionsItem } from "./ConfigurationFormEditInfo";
import ConfigurationFormEditCopy from "./ConfigurationFormEditCopy";
import ConfigurationFormEditUpdate from "./ConfigurationFormEditUpdate";
import { faExclamationTriangle, faTriangle } from "@fortawesome/pro-solid-svg-icons";

export interface IConfigurationFormEditVersionProps {
    context: IConfigurationFormEditContext<IConfigurationVersion>
}

interface IOtherVersion {
    type: ConfigurationType;
    eventKey?: string;
    eventMetadata?: IEventMetadata;
    formKey: string;
    formMetadata: IFormMetadata;
    formVersionKey: string;
    formVersionMetadata: IFormVersionMetadata;
    formVersionConfiguration: ISourceDataConfigurationFormVersion;
    configurationPropertyPath: string;
    linked: boolean;
    isGlobal: boolean;
    isTrial: boolean;
}

const ConfigurationFormEditVersion: FC<IConfigurationFormEditVersionProps> = ({
    context
}) => {
    const {
        configurationPropertyPath
    } = context;
    
    const [openOtherVersions, setOpenOtherVersions] = useState(false);
    
    const actions = useFormActions();

    const allVersions = useMemo(() => {
        if (!context.item.formMetadata?.versions || context.usingTrialConfiguration) {
            return null;
        }

        let currentVersion: IOtherVersion | undefined = undefined;

        const versionKeys = context.item.formMetadata.versions.map(x => x.id);

        const versions: IOtherVersion[] = [];

        for (let versionKey of versionKeys) {
            const versionMetadata = context.item.formMetadata?.versions?.find(x => x.id === versionKey);

            if (context.item.formEvents) {
                for (let event of context.item.formEvents[context.item.formKey!]) {
                    const localPropertyPath = `configuration.events.${event.eventKey}.forms.${context.item.formKey!}.versions.${versionKey}`
                    const localFormVersion = actions.getFieldValue<ISourceDataConfigurationFormVersion>(localPropertyPath);

                    if (localFormVersion /*&& !localFormVersion?.linked*/) {
                        const data = {
                            type: ConfigurationType.Version,
                            eventKey: event.eventKey,
                            eventMetadata: event.eventMetadata,
                            formKey: context.item.formKey!,
                            formMetadata: context.item.formMetadata!,
                            formVersionKey: versionKey!,
                            formVersionMetadata: versionMetadata!,
                            formVersionConfiguration: localFormVersion,
                            configurationPropertyPath: localPropertyPath,
                            linked: !!localFormVersion?.linked,
                            isGlobal: false,
                            isTrial: !!context.item.isTrialConfiguration
                        }

                        if (context.configurationPropertyPath !== localPropertyPath) {
                            versions.push(data)
                        }
                        else {
                            currentVersion = data;
                        }
                    }
                }
            }

            const globalPropertyPath = `configuration.forms.${context.item.formKey!}.versions.${versionKey}`;
            const globalFormVersion = actions.getFieldValue<ISourceDataConfigurationFormVersion>(globalPropertyPath);
            
            if (globalFormVersion /*&& !globalFormVersion?.linked*/) {
                const data = {
                    type: ConfigurationType.Version,
                    eventKey: undefined,
                    eventMetadata: undefined,
                    formKey: context.item.formKey!,
                    formMetadata: context.item.formMetadata!,
                    formVersionKey: versionKey!,
                    formVersionMetadata: versionMetadata!,
                    formVersionConfiguration: globalFormVersion,
                    configurationPropertyPath: globalPropertyPath,
                    linked: !!globalFormVersion?.linked,
                    isGlobal: !context.item.isTrialConfiguration,
                    isTrial: !!context.item.isTrialConfiguration
                }

                if (context.configurationPropertyPath !== globalPropertyPath) {
                    versions.push(data)
                }
                else {
                    currentVersion = data;
                }
            }

            const trialPropertyPath = `trialConfiguration.forms.${context.item.formKey!}.versions.${versionKey}`
            const trialFormVersion = actions.getFieldValue<ISourceDataConfigurationFormVersion>(trialPropertyPath);
            
            if (trialFormVersion /*&& !trialFormVersion?.linked*/) {
                const data = {
                    type: ConfigurationType.Version,
                    eventKey: undefined,
                    eventMetadata: undefined,
                    formKey: context.item.formKey!,
                    formMetadata: context.item.formMetadata!,
                    formVersionKey: versionKey!,
                    formVersionMetadata: versionMetadata!,
                    formVersionConfiguration: trialFormVersion,
                    configurationPropertyPath: trialPropertyPath,
                    linked: !!trialFormVersion?.linked,
                    isGlobal: false,
                    isTrial: true
                }

                if (context.configurationPropertyPath !== trialPropertyPath) {
                    versions.push(data)
                }
                else {
                    currentVersion = data;
                }
            }


        }

        if (versions.length === 0) {
            return null;
        }

        return {
            otherVersions: versions.sort((a, b) => {
                
                if (!a.isTrial && b.isTrial) {
                    return -1;
                }
                else if (a.isTrial && !b.isTrial) {
                    return 1;
                }

                if (!a.isGlobal && b.isGlobal) {
                    return -1;
                }
                else if (a.isGlobal && !b.isGlobal) {
                    return 1;
                }


                if (a.eventKey !== b.eventKey) {
                    return (a.eventKey ?? 'zzz') < (b.eventKey ?? 'zzz') ? -1 : 1;
                }
                
                if (a.formKey !== b.formKey) {
                    return a.formKey < b.formKey ? -1 : 1;
                }
                
                if (a.formVersionKey !== b.formVersionKey) {
                    return a.formVersionKey < b.formVersionKey ? -1 : 1;
                }
                
                return -1;
            }),
            currentVersion
        };
    }, [context.item.formMetadata?.versions, context.item, context.usingTrialConfiguration, context.usingGlobalConfiguration, actions])

    const otherVersionContent = useMemo(() => {

        if (!allVersions || !allVersions.otherVersions) {
            return null;
        }

        return (
            <OtherVersionsBox>
                {
                    allVersions.otherVersions.map((x, i, a) => {            
                        return (
                            <OtherVersionsItem key={x.configurationPropertyPath}>
                                <ConfigurationFormEditInfo 
                                    {...x}
                                    dividers
                                />
                                <ConfigurationDividerBox>
                                    <Divider />
                                </ConfigurationDividerBox>
                                <ConfigurationFlexBox sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    {
                                        !!allVersions.currentVersion && (
                                            <>
                                                <ConfigurationFormEditCopy 
                                                    target={allVersions.currentVersion}
                                                    source={x}
                                                />
                                                <ConfigurationFormEditUpdate 
                                                    target={x}
                                                    source={allVersions.currentVersion}
                                                />
                                            </>
                                        )
                                    }
                                </ConfigurationFlexBox>
                            </OtherVersionsItem>
                        )
                    })
                }
            </OtherVersionsBox>
        )
    }, [allVersions, context.item]);

    return (
        <ConfigurationFormEditPanel 
            context={context} 
            type="Version"
            typeIcon={faCodeBranch}
        >
            {
                allVersions?.otherVersions && (
                    <>
                        <ConfigurationTitle 
                            variant="h2" 
                            {...{component: Button} as any} 
                            color='inherit' 
                            sx={{ 
                                display: 'flex', 
                                padding: 0,
                                textTransform: 'none'
                            }}
                            onClick={() => setOpenOtherVersions(c => !c)}
                        >
                            Other Versions
                            <Chip 
                                label={allVersions.otherVersions.length}
                                color="primary"
                                sx={{
                                    ml: 'auto'
                                }}
                                size="small"
                            />
                            <IconButton component="div">
                                <FontAwesomeIcon icon={openOtherVersions ? faChevronUp : faChevronDown} />
                            </IconButton>
                        </ConfigurationTitle>
                        {
                            openOtherVersions && (
                                otherVersionContent
                            )
                        }
                    </>
                )
            }
            <ConfigurationTitle variant="h2">
                Options
            </ConfigurationTitle>
            <ConfigurationBox>
                <InputOnlyField 
                    label="Override Name"
                    propertyPath={`${configurationPropertyPath}.name`}
                    component={Text}
                    margin="normal"
                />
                <InputOnlyField 
                    label="Priority"
                    propertyPath={`${configurationPropertyPath}.priority`}
                    component={Select}
                    FormControlProps={{
                        margin: 'normal'
                    }}
                >
                    <MenuItem
                        value={SourceDataPriority.Low}
                    >
                        <Box sx={{display: 'flex', width: 'auto', flex: '1 1 auto'}}>
                            Low 
                            <Box sx={{ml: 'auto', color: 'success.main', transform: 'rotate(180deg)'}}>
                                <FontAwesomeIcon icon={faTriangle} />
                            </Box>
                        </Box>
                    </MenuItem>
                    <MenuItem
                        value={SourceDataPriority.Normal}
                    >
                        Normal
                    </MenuItem>
                    <MenuItem
                        value={SourceDataPriority.High}
                    >
                        <Box sx={{display: 'flex', width: 'auto', flex: '1 1 auto'}}>
                            High
                            <Box sx={{ml: 'auto', color: 'warning.main'}}>
                                <FontAwesomeIcon icon={faTriangle} />
                            </Box>
                        </Box>
                    </MenuItem>
                    <MenuItem
                        value={SourceDataPriority.Critical}
                    >
                        <Box sx={{display: 'flex', width: 'auto', flex: '1 1 auto'}}>
                            Critical
                            <Box sx={{ml: 'auto', color: 'error.main'}}>
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                            </Box>
                        </Box>
                    </MenuItem>
                </InputOnlyField>
                <InputOnlyField 
                    label="Hide Version When No Data Provided"
                    propertyPath={`${configurationPropertyPath}.excludeBlank`}
                    component={Checkbox}
                    FormControlProps={{
                        margin: 'normal'
                    }}
                />
            </ConfigurationBox>

            <ConfigurationTitle variant="h4">
                Alerts
            </ConfigurationTitle>
            <ConfigurationBox>
                <InputOnlyField 
                    label="Information"
                    propertyPath={`${configurationPropertyPath}.alertInformation`}
                    component={Markdown}
                    margin="normal"
                    //color="info"
                />
                <InputOnlyField 
                    label="Warning"
                    propertyPath={`${configurationPropertyPath}.alertWarning`}
                    component={Markdown}
                    margin="normal"
                    //color="warning"
                />
                <InputOnlyField 
                    label="Error"
                    propertyPath={`${configurationPropertyPath}.alertError`}
                    component={Markdown}
                    margin="normal"
                    //color="error"
                />
                <InputOnlyField 
                    label="Success"
                    propertyPath={`${configurationPropertyPath}.alertSuccess`}
                    component={Markdown}
                    margin="normal"
                    //color="success"
                />
            </ConfigurationBox>
        </ConfigurationFormEditPanel>
    )
}

export default ConfigurationFormEditVersion;